// Shared completion semantics for a learner support assignment ("destek ödevi"), so every
// surface — the merged /homeworks list, the dashboard Ödevler card, and the
// /practise "Önceki ödevlerim" gate — agrees on what "completed" / question-count /
// percentage mean. Previously the retired /support-assignments list screen and the
// dashboard defined "completed" differently (the dashboard checked only
// completionStatus, the list also treated a fully-solved group as done), so a
// fully-solved-but-not-yet-flagged group showed İncele on the list yet still counted
// as actionable START on the dashboard.
//
// normalizeSupportAssignmentContent (bottom of this file) is the seam that lets a grup
// ödevi ride in activeContentList / passiveContentList next to the other three kinds.
//
// The learner list payload (querySupportAssignmentDTOListByParams with studentUserProfileId)
// carries the backend's per-student completion — solvedActivityCount /
// completionPercentage / completionStatus (see catchup-parent). These helpers read
// those fields with a graceful solved-vs-total fallback so a surface never silently
// disagrees with another, and so the tabs/bars still degrade sensibly if a field is
// ever absent.

/**
 * Has this support assignment's answering window CLOSED?
 *
 * This is the SINGLE SEAM for support-assignment end-time semantics. Every learner
 * surface that asks "is it over?" — the merged Ödevler live filter, the detail
 * screen's timer label, the /practise previous-work filter, and (load-bearing)
 * the gate that decides whether /practise/support-assignments/:id may reveal the answer
 * key — must call this instead of re-deriving `endAt <= Date.now()` inline.
 *
 * WHY IT IS WORTH A FUNCTION FOR ONE COMPARISON: `endAt` is presently MIDNIGHT of the
 * last day, and the backend separately treats that as an INCLUSIVE last day by adding
 * a day's worth of millis before comparing — so the client and the server do not
 * currently agree to the hour. A sibling change in catchup-parent is making `endAt`
 * carry a real time-of-day, at which point the two agree and only THIS helper needs
 * revisiting. Callers stay correct either way.
 *
 * A missing `endAt` means "no end date" ⇒ NOT ended (never auto-expires).
 *
 * ⚠️ Where this gates the /practise answer-key view it is UI defence-in-depth ONLY. It compares against
 * `Date.now()` — the learner's own device clock — and the activity read behind that screen
 * (`queryActivityWithDataDTOListByIdList`) takes only `{ idList }`, so the server hands back answer maps
 * and solution text for a live assignment whatever the UI decides to render. The authoritative gate has
 * to live in catchup-parent on that read; it is tracked and NOT done. Do not treat this helper as proof
 * the answer key is protected, and do not delete a future server-side check as "already handled here".
 */
export const checkSupportAssignmentEnded = (endAt?: number | null): boolean =>
  endAt != null && endAt <= Date.now();

/**
 * The backend's refusal of an answer save on a destek ödevi whose window has closed
 * (catchup-parent `SupportAssignmentLifecycleGuard`). The error key arrives as the bare
 * response body, like every BaseRestControllerAdvice refusal.
 */
export const SUPPORT_ASSIGNMENT_ALREADY_ENDED_ERROR_KEY = "supportAssignmentAlreadyEnded";

/**
 * True when a failed save was refused because the destek ödevi has ended — a screen
 * left open across the end time. The caller then locks itself instead of inviting a retry.
 */
export const checkSupportAssignmentEndedRefusal = (err: unknown): boolean =>
  (err as any)?.response?.data === SUPPORT_ASSIGNMENT_ALREADY_ENDED_ERROR_KEY;

/** The same refusal for a şube ödevi (catchup-parent `AssignmentLifecycleGuard`). */
export const BRANCH_ASSIGNMENT_ALREADY_ENDED_ERROR_KEY = "branchAssignmentAlreadyEnded";

/**
 * True when a failed save was refused because the assignment — destek OR şube ödevi — has ended.
 * Returns the refusal's own error key (also its i18n key) so the caller can say why, or null for any
 * other failure.
 */
export const resolveAssignmentEndedRefusal = (err: unknown): string | null => {
  const body = (err as any)?.response?.data;
  return body === SUPPORT_ASSIGNMENT_ALREADY_ENDED_ERROR_KEY || body === BRANCH_ASSIGNMENT_ALREADY_ENDED_ERROR_KEY
    ? body
    : null;
};

export interface SupportAssignmentCompletion {
  // activityCount is the scalar question count on the list payload; activityIdList is
  // only populated on the single-support-assignment detail read.
  activityIdList?: number[];
  activityCount?: number;
  solvedActivityCount?: number;
  completionPercentage?: number;
  completionStatus?: "NOT_STARTED" | "ON_GOING" | "COMPLETED";
  // The date-derived lifecycle status (still populated) — kept only as a defensive
  // fallback for isSupportAssignmentCompleted when the per-student completion fields are
  // absent (e.g. backend not yet deployed). Not used for percentage/solved counts.
  status?: "PLANNED" | "ONGOING" | "DONE";
}

// Total question count: prefer the scalar activityCount (list payload), fall back to
// the detail-only activityIdList length so both payload shapes render a count.
export const supportAssignmentQuestionCount = (supportAssignment: SupportAssignmentCompletion): number =>
  supportAssignment.activityCount ?? supportAssignment.activityIdList?.length ?? 0;

export const supportAssignmentSolvedCount = (supportAssignment: SupportAssignmentCompletion): number =>
  supportAssignment.solvedActivityCount ?? 0;

// The single source of truth for "this student finished this support assignment": the backend
// flagged it COMPLETED, OR the student has solved every activity. Used by the list
// tabs, the dashboard actionable filter, and practise so all three agree.
// The `status === "DONE"` clause is a defensive fallback: if the per-student completion
// fields are ever absent from the payload (backend not yet deployed), the feature
// degrades to the old date-derived lifecycle bucketing instead of silently collapsing
// to "nothing is ever complete".
export const isSupportAssignmentCompleted = (supportAssignment: SupportAssignmentCompletion): boolean =>
  supportAssignment.completionStatus === "COMPLETED" ||
  supportAssignment.status === "DONE" ||
  (supportAssignmentQuestionCount(supportAssignment) > 0 &&
    supportAssignmentSolvedCount(supportAssignment) >= supportAssignmentQuestionCount(supportAssignment));

// Percentage 0..100. Prefer the backend's 0..1 completionPercentage; fall back to
// solved/total when it's absent.
export const supportAssignmentPercentage = (supportAssignment: SupportAssignmentCompletion): number => {
  if (supportAssignment.completionPercentage != null) {
    return Math.round(supportAssignment.completionPercentage * 100);
  }
  const total = supportAssignmentQuestionCount(supportAssignment);
  return total > 0
    ? Math.round((supportAssignmentSolvedCount(supportAssignment) / total) * 100)
    : 0;
};

/**
 * The per-student completion status of a support assignment, in the SAME vocabulary the
 * branch/personalized rows already carry (`completionStatus`).
 *
 * Derived from the shared completion seams above rather than read straight off the
 * payload, so a fully-solved-but-not-yet-flagged group reads COMPLETED here exactly as
 * it does on every other surface.
 */
export const supportAssignmentCompletionStatus = (
  supportAssignment: SupportAssignmentCompletion,
): "COMPLETED" | "ON_GOING" | "NOT_STARTED" => {
  if (isSupportAssignmentCompleted(supportAssignment)) return "COMPLETED";
  return supportAssignmentSolvedCount(supportAssignment) > 0
    ? "ON_GOING"
    : "NOT_STARTED";
};

/**
 * Project a support assignment onto the shape `activeContentList` speaks, so a destek ödevi
 * is just a fourth KIND inside the single Ödevler list / dashboard card / calendar
 * instead of a parallel "Çalışmalar" world.
 *
 * ⚠️ Never fabricate `beginAt` / `endAt`. A group without a deadline keeps `endAt`
 * absent, and every consumer must stay null-safe. The active filter in LearnerApp reads
 * `endAt != null ? endAt > now : type === "SUPPORT_ASSIGNMENT"` for exactly this reason:
 * plain `endAt > now` is `false` for a null and would have made such a row VANISH, but
 * the tolerance is SCOPED TO THIS KIND — `endAt` is optional on IBranchAssignment too,
 * and a branch/exam row with no end date must still expire rather than live forever.
 *
 * `isEvaluated: false` is deliberate: support assignments have no evaluation lifecycle,
 * and the active filter drops evaluated rows.
 */
export const normalizeSupportAssignmentContent = (supportAssignment: any) => {
  const total = supportAssignmentQuestionCount(supportAssignment);
  const solved = supportAssignmentSolvedCount(supportAssignment);
  return {
    ...supportAssignment,
    type: "SUPPORT_ASSIGNMENT",
    activityCount: total,
    // 0..1 fraction, matching what every other content row carries. The backend's own
    // value wins when present; otherwise solved/total, and 0 when there is nothing to solve.
    completionPercentage:
      supportAssignment.completionPercentage ?? (total > 0 ? solved / total : 0),
    completionStatus: supportAssignmentCompletionStatus(supportAssignment),
    isEvaluated: false,
  };
};

/**
 * May the learner SEE this destek ödevi's results (✓/✗ verdicts, graded answers, the
 * correct-answer reveal and the solution)?
 *
 * A destek that contains an open-ended question keeps its results hidden from learners
 * until staff publish them for the learner's şube; the learner DTO (GET /dto/{id} and the
 * learner by-params read) then carries `resultsPublished: false`. ONLY an explicit `false`
 * hides — an absent field (a backend that predates the gate) or `true` means visible,
 * exactly as before, so an older backend can never blank every learner's results.
 *
 * Completion (answered / total) is NOT gated — it counts answers, not correctness.
 */
export const checkSupportAssignmentResultsVisible = (
  resultsPublished?: boolean | null,
): boolean => resultsPublished !== false;

export interface SupportAssignmentActivityRenderFlags {
  // Passed to the content component's canAnswerQuestion.
  canAnswer: boolean;
  // The content component's isPreview — in the library this GRADES the shown answer
  // against the key (or reveals the key when nothing was picked), so it is a
  // correctness reveal, not merely a lock.
  isPreview: boolean;
  showCorrectAnswer: boolean;
  // Whether the "Çözümü göster" CTA may be offered (still subject to a solution existing).
  showSolution: boolean;
}

/**
 * How one question of a destek ödevi renders, given the runner mode, whether the learner
 * has committed an answer to it, and whether results are visible.
 *
 * With results hidden, an answered question is LOCKED but UNGRADED (canAnswer false,
 * isPreview false — the learner's own pick stays selected, no verdict colours), the
 * review mode drops its answer-key reveal, and the solution is withheld (it explains the
 * correct answer). An unanswered question stays answerable outside review mode.
 * OPEN_ENDED is the exception: its submitted answer keeps isPreview (its only lock).
 */
export const resolveSupportAssignmentActivityRenderFlags = ({
  reviewMode,
  answered,
  resultsVisible,
  templateType,
}: {
  reviewMode: boolean;
  answered: boolean;
  resultsVisible: boolean;
  templateType?: string;
}): SupportAssignmentActivityRenderFlags => {
  if (!resultsVisible) {
    return {
      canAnswer: !reviewMode && !answered,
      // OPEN_ENDED has no verdict to hide, and isPreview is its ONLY lock: its material
      // ignores canAnswerQuestion, so isPreview false would reopen a submitted answer as a
      // live textarea + upload tiles (uploads would overwrite the stored file).
      isPreview: templateType === "OPEN_ENDED" ? reviewMode || answered : false,
      showCorrectAnswer: false,
      showSolution: false,
    };
  }
  if (reviewMode) {
    return { canAnswer: true, isPreview: true, showCorrectAnswer: true, showSolution: true };
  }
  return { canAnswer: !answered, isPreview: answered, showCorrectAnswer: false, showSolution: answered };
};
